import { formatComp } from "@/utils/format";
import { Badge } from "@super-resume-new/ui/components/badge";
import { Button } from "@super-resume-new/ui/components/button";
import { Link } from "@tanstack/react-router";
import { Briefcase, Building2, DollarSign, MapPin, X } from "lucide-react";

export interface SelectedJobData {
  id: string;
  slug: string;
  title: string;
  company: string | null;
  workMode: string | null;
  locationPrimary: string | null;
  seniority: string | null;
  compMin: number | null;
  compMax: number | null;
  compCurrency: string | null;
  compPeriod: string | null;
  applyUrl?: string | null;
  rawText?: string | null;
}

interface SelectedJobCardProps {
  job: SelectedJobData;
  /** Emerald marks the ATS booster; every other screen keeps the violet default. */
  accent?: "violet" | "emerald";
  onClear: () => void;
}

export function SelectedJobCard({ job, accent = "violet", onClear }: SelectedJobCardProps) {
  const accentIcon = accent === "emerald" ? "text-emerald-500" : "text-violet-500";

  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
          <Building2 className={`size-3.5 shrink-0 ${accentIcon}`} aria-hidden="true" />
          <span className="truncate">{job.company || "Direct Listing"}</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          onClick={onClear}
          title="Switch to manual job inputs"
        >
          <X className="size-3.5" />
        </Button>
      </div>
      <Link to="/jobs/$slug" params={{ slug: job.slug }} target="_blank">
        <h3 className="text-sm font-semibold text-foreground">{job.title}</h3>
      </Link>

      {/* Badges / Metadata */}
      <div className="flex flex-wrap items-center gap-1.5 text-xs">
        {job.workMode && (
          <Badge variant="secondary" className="capitalize text-xs">
            {job.workMode}
          </Badge>
        )}
        {job.locationPrimary && (
          <Badge variant="outline" className="text-xs">
            <MapPin className="size-3 mr-1 text-muted-foreground" />
            {job.locationPrimary}
          </Badge>
        )}
        {job.seniority && (
          <Badge variant="outline" className="capitalize text-xs">
            <Briefcase className="size-3 mr-1 text-muted-foreground" />
            {job.seniority}
          </Badge>
        )}
        {job.compMin !== null && (
          <Badge
            variant="secondary"
            className="text-xs font-mono bg-emerald-500/10 text-emerald-500"
          >
            <DollarSign className="size-3 mr-0.5" />
            {formatComp(job.compMin, job.compMax, job.compCurrency, job.compPeriod)}
          </Badge>
        )}
      </div>
    </div>
  );
}
